/*!
 * Loftsails Section Modal — Relocate sections into a bottom-sheet
 * Version: 1.0.0
 */

(function() {
  'use strict';
  if (window.LoftsailsSectionModal) return;

  // Breakpoint: 769px = desktop (modal active), <=768px = mobile (inline/scroll)
  var DESKTOP_MQ = '(min-width:769px)';
  var RESTORE_FALLBACK_MS = 400;

  function resolveWrapper(targetId) {
    var n = document.getElementById(targetId)
      || document.querySelector('[data-section-id="' + targetId + '"]')
      || document.getElementById('shopify-section-' + targetId)
      || document.querySelector('[id*="' + targetId.replace('shopify-section-', '') + '"]');
    return n ? (n.closest('[id^="shopify-section-"]') || n) : null;
  }

  class LoftsailsSectionModal {
    constructor(root) {
      this.events = new AbortController();
      this.focusTimeout = null;
      this.root = root;
      this.overlay = root.querySelector('[data-lsm-overlay]');
      this.sheet = root.querySelector('[data-lsm-sheet]');
      this.body = root.querySelector('[data-lsm-body]');
      this.titleEl = root.querySelector('[data-lsm-title]');
      this.closeBtn = root.querySelector('[data-lsm-close]');

      this.placeholder = null;
      this.currentWrapper = null;
      this.lastFocus = null;
      this.isOpenState = false;
      this.restoreTimeout = null;
      this.onTransitionEnd = null;

      this.bindEvents();
      this.tagManaged();
      this.overlay.inert = true;
      this.mediaQuery = window.matchMedia(DESKTOP_MQ);
      this.mediaQuery.addEventListener('change', () => this.syncManaged(), { signal: this.events.signal });
      this.syncManaged();
      root.removeAttribute('hidden');
    }

    bindEvents() {
      var self = this;

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', function() {
          self.close();
        }, { signal: this.events.signal });
      }

      if (this.overlay) {
        this.overlay.addEventListener('click', function(e) {
          if (e.target === self.overlay) {
            self.close();
          }
        }, { signal: this.events.signal });
      }

      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && self.isOpenState) {
          self.close();
        }
        if (e.key === 'Tab' && self.isOpenState) {
          self.handleFocusTrap(e);
        }
      }, { signal: this.events.signal });

      document.addEventListener('lsm:open', function(e) {
        if (e.detail) {
          self.open(e.detail.targetId, e.detail.label);
        }
      }, { signal: this.events.signal });

      document.addEventListener('cart:change', function(e) {
        if (self.isOpenState && e.detail && e.detail.baseEvent === 'variant:add') {
          self.close();
        }
      }, { signal: this.events.signal });

      document.addEventListener('shopify:section:load', function() {
        self.reset();
      }, { signal: this.events.signal });

      document.addEventListener('shopify:section:select', function() {
        self.reset();
      }, { signal: this.events.signal });
    }

    tagManaged() {
      document.querySelectorAll('[data-target][data-modal-mode]').forEach(function(btn) {
        var mode = btn.dataset.modalMode;
        if (!mode || mode === 'never') return;

        var wrapper = resolveWrapper(btn.dataset.target);
        if (!wrapper || wrapper.hasAttribute('data-lsm-managed')) return;

        wrapper.setAttribute('data-lsm-managed', mode);
      });
    }

    open(targetId, label) {
      var wrapper = resolveWrapper(targetId);
      if (!wrapper || !this.body || !this.overlay) return false;

      if (this.isOpenState && this.currentWrapper === wrapper) {
        if (label && this.titleEl) {
          this.titleEl.textContent = label;
        }
        return true;
      }

      if (this.isOpenState) {
        this.clearRestoreTimer();
        this.restore();
        this.overlay.classList.remove('is-open');
        this.isOpenState = false;
      }

      this.lastFocus = document.activeElement;

      if (label && this.titleEl) {
        this.titleEl.textContent = label;
      }

      this.placeholder = document.createComment('lsm-placeholder');
      wrapper.parentNode.insertBefore(this.placeholder, wrapper);
      this.body.appendChild(wrapper);
      this.currentWrapper = wrapper;

      wrapper.inert = false;
      wrapper.removeAttribute('aria-hidden');
      this.overlay.inert = false;
      this.overlay.classList.add('is-open');
      this.overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.isOpenState = true;

      var self = this;
      this.focusTimeout = setTimeout(function() {
        if (self.closeBtn && self.isOpenState) {
          self.closeBtn.focus();
        }
      }, 100);

      return true;
    }

    close() {
      if (!this.isOpenState || !this.overlay) return;

      var self = this;
      clearTimeout(this.focusTimeout);
      if (this.lastFocus?.isConnected) this.lastFocus.focus({ preventScroll: true });
      this.overlay.inert = true;
      this.overlay.classList.remove('is-open');
      this.overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      function finishClose() {
        self.clearRestoreTimer();
        self.restore();
        self.isOpenState = false;
        if (self.lastFocus && typeof self.lastFocus.focus === 'function') {
          self.lastFocus.focus();
        }
      }

      if (this.sheet) {
        this.onTransitionEnd = function(e) {
          if (e.target !== self.sheet) return;
          self.sheet.removeEventListener('transitionend', self.onTransitionEnd);
          self.onTransitionEnd = null;
          finishClose();
        };
        this.sheet.addEventListener('transitionend', this.onTransitionEnd);
      }

      this.restoreTimeout = setTimeout(finishClose, RESTORE_FALLBACK_MS);
    }

    clearRestoreTimer() {
      clearTimeout(this.restoreTimeout);
      this.restoreTimeout = null;
      if (this.sheet && this.onTransitionEnd) {
        this.sheet.removeEventListener('transitionend', this.onTransitionEnd);
        this.onTransitionEnd = null;
      }
    }

    restore() {
      if (!this.currentWrapper) return;

      if (this.placeholder && this.placeholder.parentNode) {
        this.placeholder.parentNode.insertBefore(this.currentWrapper, this.placeholder);
        this.placeholder.remove();
      } else {
        document.body.appendChild(this.currentWrapper);
      }

      this.placeholder = null;
      this.currentWrapper = null;
      this.syncManaged();
    }

    syncManaged() {
      const desktop = window.matchMedia(DESKTOP_MQ).matches;
      document.querySelectorAll('[data-lsm-managed]').forEach(wrapper => {
        const mode = wrapper.dataset.lsmManaged;
        const hidden = wrapper !== this.currentWrapper && (mode === 'all' || (mode === 'desktop' && desktop) || (mode === 'mobile' && !desktop));
        if (hidden && wrapper.contains(document.activeElement)) document.activeElement.blur();
        wrapper.inert = hidden;
        if (hidden) wrapper.setAttribute('aria-hidden', 'true');
        else wrapper.removeAttribute('aria-hidden');
      });
    }

    destroy() {
      this.reset();
      clearTimeout(this.focusTimeout);
      this.events.abort();
    }

    reset() {
      this.clearRestoreTimer();
      this.restore();
      if (this.overlay) {
        this.overlay.classList.remove('is-open');
        this.overlay.inert = true;
        this.overlay.setAttribute('aria-hidden', 'true');
      }
      document.body.style.overflow = '';
      this.isOpenState = false;
      this.lastFocus = null;
      this.tagManaged();
      this.syncManaged();
    }

    handleFocusTrap(e) {
      if (!this.sheet) return;

      var focusable = [...this.sheet.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter(element => !element.closest('[hidden], [inert], [aria-hidden="true"]') && element.getClientRects().length);

      if (focusable.length === 0) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    isOpen() {
      return this.isOpenState;
    }
  }

  var instance = null;

  function init() {
    var root = document.querySelector('[data-lsm-root]');
    if (instance?.root === root) return;
    instance?.destroy();
    instance = null;
    if (!root) return;
    instance = new LoftsailsSectionModal(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function() {
    init();
  });
  document.addEventListener('loftsails:product:updated', init);
  document.addEventListener('shopify:section:unload', event => {
    if (instance && event.target.contains(instance.root)) { instance.destroy(); instance = null; }
  });

  window.LoftsailsSectionModal = {
    getInstance: function() {
      return instance;
    }
  };
})();
